/**
 * Setting how many of a dish are left, as a page.
 *
 * This was a dialog. It holds one number, which makes it the clearest case of
 * the problem: the keyboard covers the lower half of a phone, and a dialog
 * renders through a portal, outside the screen that knows how much is covered.
 * A page gets that from `Screen`, and the field, the heading and the action all
 * stay in view without any of them being corrected by hand.
 */

import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CircleHelp } from 'lucide-react-native';
import { fetchMerchantMenu, setInventory } from '@yummy/db';
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  LoadingState,
  PageHeading,
  Row,
  Screen,
  Stack,
  Text,
  TextField,
  useAction,
  useAsync,
  useLocale,
  useTheme,
  useToast,
} from '@yummy/ui-rnr';

import { FooterBar } from '../components/FooterBar';
import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';

/**
 * Renders the remaining-stock form.
 *
 * @returns The stock page.
 */
export function StockFormScreen() {
  const { itemId } = useParams<{ itemId: string }>();
  const { t } = useLocale();
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const action = useAction();
  const menu = useAsync(
    async () => (restaurantId ? await fetchMerchantMenu(supabase, restaurantId) : null),
    [restaurantId],
  );

  const item = useMemo(
    () => menu.data?.items.find((candidate) => candidate.id === itemId),
    [menu.data, itemId],
  );

  // Seeded from the item once the menu arrives, so a deep link lands on the
  // current count rather than an empty field.
  const [count, setCount] = useState<string | null>(null);
  const value = count ?? String(item?.inventory_count ?? 0);

  // Back to the category this was opened from, not to the whole menu. Without
  // the category the list comes back on All and the merchant has to find their
  // place again. Read from the URL rather than passed down, so it survives a
  // reload of the form itself.
  const [searchParams] = useSearchParams();
  const returnCategory = searchParams.get('category');
  const back = useCallback(
    () => navigate(returnCategory ? `/menu?category=${returnCategory}` : '/menu'),
    [navigate, returnCategory],
  );

  const save = useCallback(async () => {
    if (!item) return;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return;

    const result = await action.run(() => setInventory(supabase, item.id, Math.trunc(parsed)));
    if (result) {
      toast.show(
        t('merchant.menu.stockSet', { name: result.name, count: Math.trunc(parsed) }),
        { tone: 'success' },
      );
      back();
    }
  }, [item, value, action, toast, t, back]);

  if (menu.loading) return <LoadingState />;

  if (!item) {
    return (
      <Screen>
        <EmptyState
          icon={CircleHelp}
          title={t('common.notFound')}
          actionLabel={t('merchant.nav.menu')}
          onAction={back}
        />
      </Screen>
    );
  }

  return (
    <Screen
      footer={
        <FooterBar>
          <Row gap="sm" justify="end">
            <Button variant="secondary" onPress={back} accessibilityLabel={t('common.cancel')}>
              <Text>{t('common.cancel')}</Text>
            </Button>
            <Button
              onPress={() => void save()}
              disabled={action.pending}
              accessibilityLabel={t('common.save')}
              aria-busy={action.pending}>
              {action.pending ? (
                <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
              ) : (
                <Text>{t('common.save')}</Text>
              )}
            </Button>
          </Row>
        </FooterBar>
      }>
      <Stack gap="xl">
        <PageHeading
          title={t('merchant.menu.stockTitle', { name: item.name })}
          subtitle={t('merchant.menu.stockSubtitle')}
        />

        <Card className="p-4">
          <Stack gap="lg">
            <TextField
              label={t('merchant.menu.stockRemaining')}
              value={value}
              onChangeText={setCount}
              keyboardType="number-pad"
              autoFocus
              returnKeyType="done"
              onSubmitEditing={() => void save()}
            />

            {action.error ? (
              <ErrorBanner message={action.error} onRetry={() => void save()} />
            ) : null}
          </Stack>
        </Card>
      </Stack>
    </Screen>
  );
}
